import React from 'react';
import { useApp } from '../context/AppContext';
import { X, AlertTriangle, CheckCircle, Droplets, Zap } from 'lucide-react';

const FiberDetailModal = ({ fiber, onClose }) => {
  if (!fiber) return null;

  const isNatural = fiber.type === 'natural';
  const getScoreColor = (score) => {
    if (score >= 70) return '#16A34A';
    if (score >= 40) return '#EAB308';
    return '#DC2626';
  };

  // Mock detailed fiber data (would come from fabric_database in production)
  const details = {
    risks: isNatural 
      ? ['High water usage (if not organic)', 'Pesticide concerns (conventional)']
      : ['Non-biodegradable', 'Microplastic shedding', 'Fossil fuel based'],
    benefits: isNatural
      ? ['Biodegradable', 'Breathable', 'Natural comfort']
      : ['Durable', 'Wrinkle resistant', 'Quick drying'],
    lifespan: isNatural ? '3-5 years' : '5-10 years',
    care: isNatural ? 'Gentle wash, air dry' : 'Machine washable, tumble dry'
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end justify-center"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-t-3xl w-full max-w-md p-6 animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-black uppercase">{fiber.name}</h2>
          <button onClick={onClose} className="w-8 h-8 bg-black/5 rounded-full flex items-center justify-center">
            <X size={16} />
          </button>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-black/55 uppercase font-bold">Quality Score</span>
            <span className="text-2xl font-bold" style={{ color: getScoreColor(fiber.score) }}>{fiber.score}/100</span>
          </div>
          <div className="h-2 bg-black/10 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full" 
              style={{ width: fiber.score + '%', backgroundColor: getScoreColor(fiber.score) }}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-xs uppercase font-bold text-black/55 mb-2">Environmental Risks</p>
            <ul className="space-y-1">
              {details.risks.map((risk, i) => (
                <li key={i} className="text-sm text-black/72 flex items-start gap-2">
                  <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                  <span>{risk}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs uppercase font-bold text-black/55 mb-2">Benefits</p>
            <ul className="space-y-1">
              {details.benefits.map((benefit, i) => (
                <li key={i} className="text-sm text-black/72 flex items-start gap-2">
                  <CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-between text-sm pt-4 border-t border-black/10">
            <span className="text-black/55">Expected Lifespan:</span>
            <span className="font-bold text-black">{details.lifespan}</span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-black/55">Care:</span>
            <span className="font-bold text-black">{details.care}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiberDetailModal;
